import {
	ADDRESSES,
	DEFAULT_PRICE_IMPACT_DELTA_PERCENT,
	MIN_MARGIN_AMOUNT_V3,
	PERIOD_IN_SECONDS,
	Period,
	SL_TP_MAX_SIZE_CROSS_MARGIN,
	V3SynthMarketKey,
	V3_SYNTH_MARKET_IDS,
} from '@kwenta/sdk/constants'
import {
	CollateralTransfer,
	ConditionalOrderInput,
	ConditionalOrderStatusV3,
	ConditionalOrderTypeEnum,
	ConditionalOrderV3Response,
	FuturesMarketAsset,
	FuturesMarketKey,
	FuturesOrderHistory,
	FuturesVolumes,
	MarginSnapshot,
	NetworkId,
	PerpsMarketV3,
	PerpsV2Position,
	PerpsV3AsyncOrder,
	PerpsV3Liquidation,
	PerpsV3Position,
	PerpsV3PositionHistory,
	PerpsV3Trade,
	PnlSnapshot,
	PositionSide,
	PotentialTradeStatus,
	RawCondition,
	SmartMarginOrderType,
	TransactionStatus,
} from '@kwenta/sdk/types'
import {
	calculateDesiredFillPrice,
	floorNumber,
	getDefaultPriceImpact,
	isSimulatedRequest,
	wrappedTokenToSynth,
} from '@kwenta/sdk/utils'
import Wei, { wei } from '@kwenta/wei'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { getAddress } from 'ethers/lib/utils.js'
import { debounce } from 'lodash'
import { formatUnits } from 'viem'

import { notifyError } from 'components/ErrorNotifier'
import { DEFAULT_MAX_EXECUTOR_FEE_USD, SNX_V3_ENABLED } from 'constants/futures'
import { DepositableAssetKeysV3 } from 'constants/perpsV3'
import {
	monitorAndAwaitTransaction,
	monitorAndAwaitUserOp,
	monitorFollowingTransaction,
} from 'state/app/helpers'

import {
	handleTransactionError,
	setOpenModal,
	setShowPositionModal,
	setTransaction,
	updateTransactionHash,
	updateTransactionStatus,
	updateTransactionStep,
} from 'state/app/reducer'
import { fetchTokenAllowances, fetchV3BalancesAndAllowances } from 'state/balances/actions'
import { selectTokenAllowances } from 'state/balances/selectors'
import {
	selectAddressBook,
	selectEditPositionPreview,
	selectNewNickname,
	selectOneClickReady,
	selectSelectedDelegationAddress,
	selectSlTpModalInputs,
} from 'state/futures/selectors'
import { AppThunk } from 'state/store'
import { ThunkConfig } from 'state/types'
import {
	selectProviderNetwork,
	selectSignerNetwork,
	selectSignerWallet,
	selectWallet,
} from 'state/wallet/selectors'
import { currentTimestamp } from 'utils/date'
import { isUserDeniedError } from 'utils/formatters/error'
import {
	orderConditionFromTargetPrice,
	orderPriceInvalidLabel,
	serializeFuturesVolumes,
	serializePerpsV3PositionHistory,
	serializeTransactionOrder,
	serializeV3AsyncOrder,
	serializeV3Liquidations,
	serializeV3Market,
	serializeV3Positions,
	serializeV3Trades,
} from 'utils/futures'
import logError from 'utils/logError'

import { fetchAbstractionUsdcBalance } from '../actions'
import {
	selectAbstractionUsdcEngineAllowance,
	selectClosePositionOrderInputs,
	selectEditPositionInputs,
	selectLeverageSide,
	selectMarketIndexPrice,
	selectPerpsProvider,
	selectTradeOrderType,
	selectTradePanelInputs,
	selectTradePanelOrderPrice,
} from '../common/selectors'
import {
	AccountContext,
	AppFuturesMarginType,
	DebouncedCMPreviewParams,
	PreviewAction,
	SnxPerpsV3TradePreviewParams,
} from '../common/types'
import {
	addDelegateToAddressBook,
	removeDelegateFromAddressBook,
	setAbstractionUsdcEngineAllowance,
	setClosePositionPrice,
	setClosePositionSizeDelta,
	setEditPositionInputs,
	setLeverageInput,
	setLeverageSide,
	setOrderPriceInvalidLabel,
	setTradeInputs,
	setTradePanelOrderPrice,
} from '../reducer'
import { DelegationAccountInfo } from '../types'

import {
	addDelegateToCMDelegates,
	handlePreviewError,
	incrementPreviewCrossMarginCount,
	removeDelegateFromCMDelegates,
	setCrossMarginTradePreview,
	setPerpsV3Account,
	setPerpsV3Spenders,
} from './reducer'
import {
	selectAccountContext,
	selectAllCrossMarginSLTPOrders,
	selectAsyncCrossMarginOrder,
	selectCloseCMPositionPreview,
	selectCloseModalKeeperDepositV3,
	selectCondOrdersEnabled,
	selectCrossMarginAccount,
	selectCrossMarginAvailableMargin,
	selectCrossMarginConditionalOrders,
	selectCrossMarginPositions,
	selectCrossMarginTradePreview,
	selectCrossMarginTradeableMargin,
	selectDepositAllowances,
	selectMarginEnginePermitted,
	selectMargineEngineEnabled,
	selectMarkPricesV3,
	selectProviderSupportsCrossMargin,
	selectReservedMarginForOrders,
	selectSLTPModalUsdDepositV3,
	selectSignerSupportsCrossMargin,
	selectTradePanelKeeperDepositV3,
	selectV3EditPositionModalInfo,
	selectV3Markets,
	selectV3SelectedMarketId,
	selectV3SelectedMarketInfo,
	selectV3SkewAdjustedPrice,
	selectSnxV3GlobalTradesForMarket,
} from './selectors'
import { MarginInfo, SnxPerpsV3TradePreview } from './types'

export const fetchV3Markets = createAsyncThunk<
	{ markets: PerpsMarketV3<string>[]; networkId: NetworkId } | undefined,
	void,
	ThunkConfig
>('futures/fetchV3Markets', async (_, { getState, extra: { sdk } }) => {
	const supportedNetwork = selectProviderSupportsCrossMargin(getState())
	const networkId = selectProviderNetwork(getState())
	if (!supportedNetwork || !SNX_V3_ENABLED) return
	try {
		const v3Markets = await sdk.perpsV3.getMarkets()
		return { markets: v3Markets.map(serializeV3Market), networkId }
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch markets', err)
		throw err
	}
})

export const fetchCrossMarginMarketData = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchCrossMarginMarketData',
	async (_, { dispatch }) => {
		await dispatch(fetchV3Markets())
		dispatch(fetchDailyVolumesV3())
	}
)

export const fetchPerpsV3Account = createAsyncThunk<
	| { accountId: string; wallet: string; marginEnginePermitted: boolean; network: NetworkId }
	| undefined,
	boolean | void,
	ThunkConfig
>('futures/fetchPerpsV3Account', async (refetch, { getState, extra: { sdk }, rejectWithValue }) => {
	const wallet = selectWallet(getState())
	const supportedNetwork = selectProviderSupportsCrossMargin(getState())
	const network = selectProviderNetwork(getState())
	const marginEngineEnabled = selectMargineEngineEnabled(getState())

	if (!wallet || !supportedNetwork) return undefined

	try {
		// TODO: Support multiple accounts
		const accounts = await sdk.perpsV3.getAccounts(wallet, marginEngineEnabled)

		if (!accounts.length) return undefined
		const { accountId, marginEnginePermitted } = accounts[0]
		return {
			accountId: accountId.toString(),
			marginEnginePermitted,
			wallet,
			network,
		}
	} catch (err) {
		notifyError('Failed to fetch cross margin account', err)
		rejectWithValue(err.message)
	}
})

export const fetchDailyVolumesV3 = createAsyncThunk<FuturesVolumes<string>, void, ThunkConfig>(
	'futures/fetchDailyVolumesV3',
	async (_, { extra: { sdk } }) => {
		try {
			const volumes = await sdk.perpsV3.getDailyVolumes()
			return serializeFuturesVolumes(volumes)
		} catch (err) {
			logError(err)
			notifyError('Failed to fetch daily volumes', err)
			throw err
		}
	}
)

export const fetchKeeperBalance = createAsyncThunk<
	{ balance: string; network: NetworkId; wallet: string } | undefined,
	void,
	ThunkConfig
>('futures/fetchKeeperBalance', async (_, { getState, extra: { sdk } }) => {
	const accountId = selectCrossMarginAccount(getState())
	const network = selectProviderNetwork(getState())
	const wallet = selectWallet(getState())
	const marginEngineEnabled = selectMargineEngineEnabled(getState())

	if (!accountId || !wallet || !marginEngineEnabled) return
	try {
		const balance = await sdk.perpsV3.getAccountUsdcCredit(accountId)
		return { balance: balance.toString(), wallet, network }
	} catch (err) {
		notifyError('Failed to fetch account ETH balance', err)
		throw err
	}
})

export const fetchPerpsV3Balances = createAsyncThunk<void, void, ThunkConfig>(
	'balances/fetchPerpsV3Balances',
	async (_, { dispatch, extra: { sdk } }) => {
		const marketProxy = sdk.context.defaultContractConfigs.PerpsV3MarketProxy?.address
		const marginEngine = sdk.context.defaultContractConfigs.MarginEngine?.address
		const spotProxy = sdk.context.defaultContractConfigs.SpotV3MarketProxy?.address
		if (marketProxy && spotProxy) {
			const spenders = [marketProxy, spotProxy]
			if (marginEngine) spenders.push(marginEngine)
			dispatch(fetchV3BalancesAndAllowances(spenders))
			// For wrapped USDC on Base
			dispatch(fetchTokenAllowances({ tokenSymbols: ['USDC'], spenders: spenders }))
			dispatch(
				setPerpsV3Spenders({
					marginEngine: marginEngine,
					marketProxy: marketProxy,
					spotProxy: spotProxy,
				})
			)
			dispatch(fetchKeeperBalance())
		}
	}
)

export const fetchCrossMarginPositions = createAsyncThunk<
	{ positions: PerpsV3Position<string>[]; account: string; network: NetworkId } | undefined,
	void,
	ThunkConfig
>('futures/fetchCrossMarginPositions', async (_, { extra: { sdk }, getState }) => {
	const supportedNetwork = selectProviderSupportsCrossMargin(getState())
	const { network, accountId } = selectAccountContext(getState())
	const markets = selectV3Markets(getState())
	const wallet = selectWallet(getState())

	if (!supportedNetwork || !accountId || !wallet) return
	try {
		// TODO: Map markets when subgraph updated

		const positions = await sdk.perpsV3.getPositions(
			accountId,
			markets.map((m) => BigInt(m.marketId))
		)

		return { positions: serializeV3Positions(positions), account: accountId.toString(), network }
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch cross margin positions', err)
		throw err
	}
})

export const fetchPositionHistoryV3 = createAsyncThunk<
	| {
			history: PerpsV3PositionHistory<string>[]
			account: string
			wallet: string
			networkId: NetworkId
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchPositionHistoryV3', async (_, { getState, extra: { sdk } }) => {
	try {
		const { wallet, network, accountId } = selectAccountContext(getState())
		const futuresSupported = selectProviderSupportsCrossMargin(getState())

		if (!wallet || !accountId || !futuresSupported) return
		const history = await sdk.perpsV3.getPositionHistory(accountId.toString())
		return {
			account: accountId.toString(),
			wallet,
			networkId: network,
			history: serializePerpsV3PositionHistory(history),
		}
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch perps v3 position history', err)
		throw err
	}
})

export const fetchPositionHistoryV3ForTrader = createAsyncThunk<
	{ history: PerpsV3PositionHistory<string>[]; address: string; networkId: NetworkId } | undefined,
	string,
	ThunkConfig
>(
	'futures/fetchV3PositionHistoryForTrader',
	async (traderAddress, { getState, extra: { sdk } }) => {
		try {
			const supportedNetwork = selectProviderSupportsCrossMargin(getState())
			const networkId = selectProviderNetwork(getState())

			if (!supportedNetwork) return

			const history = await sdk.perpsV3.getPositionHistory(traderAddress, 'eoa')
			return {
				history: serializePerpsV3PositionHistory(history),
				networkId,
				address: traderAddress,
			}
		} catch (err) {
			notifyError('Failed to fetch history for trader ' + traderAddress, err)
			throw err
		}
	}
)

export const fetchCrossMarginAccountTrades = createAsyncThunk<
	| {
			trades: PerpsV3Trade<string>[]
			account: string
			wallet: string
			networkId: NetworkId
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchCrossMarginAccountTrades', async (_, { getState, extra: { sdk } }) => {
	try {
		const { wallet, network, accountId } = selectAccountContext(getState())
		const futuresSupported = selectProviderSupportsCrossMargin(getState())
		if (!wallet || !accountId || !futuresSupported) return
		const trades = await sdk.perpsV3.getTradesForAccount(accountId)
		return {
			wallet,
			account: accountId.toString(),
			networkId: network,
			trades: serializeV3Trades(trades.map((t) => ({ ...t }))),
		}
	} catch (err) {
		notifyError('Failed to fetch perps v3 position history', err)
		throw err
	}
})

export const fetchSnxV3GlobalTradesForMarket = createAsyncThunk<
	| {
			trades: PerpsV3Trade<string>[]
			networkId: NetworkId
			marketId: string
	  }
	| undefined,
	| {
			nextPage?: boolean
	  }
	| undefined,
	ThunkConfig
>('futures/fetchSnxV3GlobalTradesForMarket', async (params, { getState, extra: { sdk } }) => {
	try {
		const { network } = selectAccountContext(getState())
		const marketId = selectV3SelectedMarketInfo(getState())?.marketId
		const crossMarginSupported = selectProviderSupportsCrossMargin(getState())
		const existingTrades = selectSnxV3GlobalTradesForMarket(getState())
		if (!crossMarginSupported || !marketId) return
		const oldestTrade = existingTrades?.[existingTrades.length - 1]
		const maxTimestamp = params?.nextPage
			? oldestTrade?.timestamp ?? currentTimestamp()
			: currentTimestamp()
		const minTimestamp = 0
		const trades = await sdk.perpsV3.getAllTradesByMarket(marketId, {
			minTimestamp,
			maxTimestamp,
			pageSize: 50,
		})
		return {
			networkId: network,
			marketId,
			trades: serializeV3Trades(trades.map((t) => ({ ...t }))),
		}
	} catch (err) {
		notifyError('Failed to fetch perps v3 global trades', err)
		throw err
	}
})

export const fetchCrossMarginAccountLiquidations = createAsyncThunk<
	| {
			liquidations: PerpsV3Liquidation<string>[]
			account: string
			wallet: string
			networkId: NetworkId
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchCrossMarginAccountLiquidations', async (_, { getState, extra: { sdk } }) => {
	try {
		const { wallet, network, accountId } = selectAccountContext(getState())
		const futuresSupported = selectProviderSupportsCrossMargin(getState())
		if (!wallet || !accountId || !futuresSupported) return
		const liquidations = await sdk.perpsV3.getAllAccountLiquidations(accountId)
		return {
			wallet,
			account: accountId.toString(),
			networkId: network,
			liquidations: serializeV3Liquidations(liquidations.map((t) => ({ ...t }))),
		}
	} catch (err) {
		notifyError('Failed to fetch perps v3 liquidations for account', err)
		throw err
	}
})

export const fetchPerpsV3GlobalLiquidations = createAsyncThunk<
	| {
			liquidations: PerpsV3Liquidation<string>[]
			networkId: NetworkId
			marketId: string
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchPerpsV3GlobalLiquidations', async (_, { getState, extra: { sdk } }) => {
	try {
		const { network } = selectAccountContext(getState())
		const marketId = selectV3SelectedMarketId(getState())
		const crossMarginSupported = selectProviderSupportsCrossMargin(getState())
		if (!crossMarginSupported || !marketId) return
		const maxTs = currentTimestamp()
		const minTs = 0
		const liquidations = await sdk.perpsV3.getAllLiquidationsByMarket(marketId, minTs, maxTs)
		return {
			networkId: network,
			marketId,
			liquidations: serializeV3Liquidations(liquidations.map((t) => ({ ...t }))),
		}
	} catch (err) {
		notifyError('Failed to fetch perps v3 liquidations', err)
		throw err
	}
})

export const refetchPosition = createAsyncThunk<
	{
		position: PerpsV2Position<string>
		wallet: string
		futuresType: AppFuturesMarginType
		networkId: NetworkId
	} | null,
	void,
	ThunkConfig
>('futures/refetchPosition', async () => {
	// TODO: Refetch positions
	return null
})

export const fetchCrossMarginPendingOrders = createAsyncThunk<
	| {
			order: PerpsV3AsyncOrder<string> | undefined
			wallet: string
			networkId: NetworkId
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchCrossMarginPendingOrders', async (_, { dispatch, getState, extra: { sdk } }) => {
	const { accountId, network, wallet } = selectAccountContext(getState())
	const supportedNetwork = selectProviderSupportsCrossMargin(getState())
	const existingOrder = selectAsyncCrossMarginOrder(getState())
	if (!accountId || !supportedNetwork || !wallet) return
	try {
		const order = await sdk.perpsV3.getPendingAsyncOrder(accountId)
		const orderChanged = existingOrder?.executableStartTime !== order?.settlementTime
		if (orderChanged) {
			dispatch(fetchCrossMarginPositions())
			dispatch(removeStaleOrders())
		}

		return {
			networkId: network,
			order: order ? serializeV3AsyncOrder(order) : undefined,
			wallet: wallet,
		}
	} catch (err) {
		notifyError('Failed to fetch open orders', err)
		throw err
	}
})

export const fetchCrossMarginTradePreview = createAsyncThunk<
	{ preview: SnxPerpsV3TradePreview<string> | undefined; type: PreviewAction },
	DebouncedCMPreviewParams,
	ThunkConfig
>('futures/fetchCrossMarginTradePreview', async (params, { getState, extra: { sdk } }) => {
	const market = params.market
	const availableMargin = selectCrossMarginAvailableMargin(getState())
	const accountId = selectCrossMarginAccount(getState())
	const positions = selectCrossMarginPositions(getState())
	const reservedMargin = selectReservedMarginForOrders(getState())
	const position = positions.find((p) => p.market.marketKey === market.marketKey)

	try {
		if (!accountId) return { preview: undefined, type: params.action }
		if (!market) throw new Error('No market selected')
		if (!market.settlementStrategies[0]) throw new Error('No settlement strategy found')
		const preview = await sdk.perpsV3.getTradePreview(
			accountId,
			market.marketId,
			params.sizeDelta,
			params.indexPrice
		)

		const size =
			(position?.details?.side === PositionSide.LONG
				? position.details.size
				: position?.details?.size.neg()) ?? wei(0)

		// Price impact on limit orders are only a rough indication as
		// it it is only accurate for the current market price
		const priceImpact = preview.fillPrice.sub(params.marketPrice).div(params.marketPrice).abs()
		const notional = preview.fillPrice.mul(params.sizeDelta).abs()
		const tradeableMargin = availableMargin.sub(reservedMargin)
		const newSize = size.add(params.sizeDelta) ?? params.sizeDelta

		const fillPrice = params.isConditional ? params.orderPrice : preview.fillPrice
		const populatedPreview = {
			settlementFee: preview.settlementFee.toString(),
			marketId: market.marketId,
			fee: preview.fee.toString(),
			requiredMargin: preview.requiredMargin.toString(),
			fillPrice: fillPrice.toString(),
			priceImpact: priceImpact.toString(),
			newSize: newSize.toString(),
			liqPrice: '0',
			exceedsPriceProtection: priceImpact.mul(100).gt(getDefaultPriceImpact('market')),
			sizeDelta: params.sizeDelta.toString(),
			leverage: notional.gt(0) ? availableMargin.div(notional).toNumber() : 0,
			notionalValue: notional.toString(),
			side: params.sizeDelta.gt(0) ? PositionSide.LONG : PositionSide.SHORT,
			marketKey: market.marketKey,
			status: preview.requiredMargin.gt(tradeableMargin)
				? tradeableMargin.lt(MIN_MARGIN_AMOUNT_V3)
					? PotentialTradeStatus.INSUFFICIENT_FREE_MARGIN
					: PotentialTradeStatus.MAX_POSITION_SIZE_EXCEEDED
				: PotentialTradeStatus.OK,
		}
		return { preview: populatedPreview, type: params.action }
	} catch (err) {
		notifyError(err.message, err)
		throw err
	}
})

export const debouncedPrepareCrossMarginTradePreview = debounce(
	(dispatch, inputs: DebouncedCMPreviewParams) => {
		dispatch(fetchCrossMarginTradePreview(inputs))
	},
	500
)

export const fetchMarginTransfersV3 = createAsyncThunk<
	| {
			accountTransfers: CollateralTransfer[]
			context: AccountContext
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchMarginTransfersV3', async (_, { getState, extra: { sdk } }) => {
	const perpsProvider = selectPerpsProvider(getState())
	const supportedNetwork = selectSignerSupportsCrossMargin(getState())
	const { network, accountId: cmAccount, wallet } = selectAccountContext(getState())
	if (!wallet || !supportedNetwork || !cmAccount) return
	try {
		const accountTransfers = await sdk.perpsV3.getCollateralTransfers(cmAccount)

		return {
			accountTransfers,
			context: {
				wallet: wallet,
				network: network,
				provider: perpsProvider,
				cmAccount: cmAccount?.toString(),
			},
		}
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch margin transfers', err)
		throw err
	}
})

export const fetchAccountMarginInfo = createAsyncThunk<
	{ wallet: string; network: NetworkId; marginInfo: MarginInfo<string> } | undefined,
	void,
	ThunkConfig
>('futures/fetchAccountMarginInfo', async (_, { getState, extra: { sdk } }) => {
	const supportedNetwork = selectProviderSupportsCrossMargin(getState())
	const { wallet, network, accountId } = selectAccountContext(getState())

	if (!supportedNetwork || !wallet || !accountId) return
	try {
		const {
			availableMargin,
			withdrawableMargin,
			requiredInitialMargin,
			requiredMaintenanceMargin,
			maxLiquidationReward,
		} = await sdk.perpsV3.getAccountMarginInfo(accountId)

		return {
			wallet,
			network,
			marginInfo: {
				availableMargin: availableMargin.toString(),
				withdrawableMargin: withdrawableMargin.toString(),
				requiredInitialMargin: requiredInitialMargin.toString(),
				requiredMaintenanceMargin: requiredMaintenanceMargin.toString(),
				maxLiquidationReward: maxLiquidationReward.toString(),
			},
		}
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch available margin', err)
		throw err
	}
})

type CollateralBalancesMap = Partial<Record<V3SynthMarketKey, string>>

export const fetchAccountCollateralBalances = createAsyncThunk<
	{ wallet: string; network: NetworkId; balancesMap: CollateralBalancesMap } | undefined,
	void,
	ThunkConfig
>('futures/fetchAccountCollateralBalances', async (_, { getState, extra: { sdk } }) => {
	const supportedNetwork = selectProviderSupportsCrossMargin(getState())
	const { wallet, network, accountId } = selectAccountContext(getState())

	if (!supportedNetwork || !wallet || !accountId) return
	try {
		const balances = await sdk.perpsV3.getCollateralBalances(
			accountId,
			Object.keys(V3_SYNTH_MARKET_IDS) as V3SynthMarketKey[]
		)
		const balancesMap = balances.reduce<CollateralBalancesMap>((acc, cur) => {
			acc[cur.synthId] = cur.amount.toString()
			return acc
		}, {})

		return {
			wallet,
			network,
			balancesMap,
		}
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch collateral balances', err)
		throw err
	}
})

export const fetchCrossMarginAccountData = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchCrossMarginAccountData',
	async (_, { dispatch }) => {
		dispatch(fetchPerpsV3Balances())
		dispatch(fetchAccountMarginInfo())
		dispatch(fetchAccountCollateralBalances())
		dispatch(fetchCrossMarginPositions())
		dispatch(fetchCrossMarginPendingOrders())
		dispatch(fetchConditionalOrders())
	}
)

export const removeStaleOrders = createAsyncThunk<void, void, ThunkConfig>(
	'futures/removeStaleOrders',
	async (_, { dispatch, getState, extra: { sdk } }) => {
		const { accountId } = selectAccountContext(getState())
		if (accountId) {
			await sdk.perpsV3.removeStaleConditionalOrders(accountId)
			dispatch(fetchConditionalOrders())
		}
	}
)

export const fetchConditionalOrders = createAsyncThunk<
	| {
			orders: ConditionalOrderV3Response[]
			networkId: NetworkId
			accountId: string
			wallet: string
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchConditionalOrders', async (_, { getState, extra: { sdk } }) => {
	const supportedNetwork = selectProviderSupportsCrossMargin(getState())
	const condOrdersEnabled = selectCondOrdersEnabled(getState())
	const { network: networkId, accountId, wallet } = selectAccountContext(getState())
	if (!wallet || !accountId || !supportedNetwork || !condOrdersEnabled) return

	try {
		const orders = await sdk.perpsV3.getConditionalOrders(accountId)
		return { orders: orders, networkId, accountId: accountId.toString(), wallet }
	} catch (err) {
		logError(err)
		notifyError('Failed to fetch conditional orders', err)
		throw err
	}
})

export const fetchPnlSnapshotsV3 = createAsyncThunk<
	{ snapshots: PnlSnapshot<string>[]; networkId: NetworkId; wallet: string } | undefined,
	void,
	ThunkConfig
>('futures/fetchPnlSnapshotsV3', async (_, { getState, extra: { sdk } }) => {
	const supportedNetwork = selectProviderSupportsCrossMargin(getState())
	const { network: networkId, accountId, wallet } = selectAccountContext(getState())
	if (!wallet || !accountId || !supportedNetwork) return

	try {
		const snapshots = await sdk.perpsV3.getPnlSnapshots(accountId)
		const serialized = snapshots.map((s) => ({
			...s,
			pnl: s.pnl.toString(),
		}))
		return { snapshots: serialized, networkId, wallet }
	} catch (err) {
		logError(err)
		throw err
	}
})

export const fetchMarginSnapshotsV3 = createAsyncThunk<
	| {
			snapshots: {
				[Period.ONE_YEAR]: MarginSnapshot<string>[]
				[Period.ONE_MONTH]: MarginSnapshot<string>[]
				[Period.ONE_WEEK]: MarginSnapshot<string>[]
			}
			networkId: NetworkId
			wallet: string
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchMarginSnapshotsV3', async (_, { getState, extra: { sdk } }) => {
	const supportedNetwork = selectProviderSupportsCrossMargin(getState())
	const { network: networkId, accountId, wallet } = selectAccountContext(getState())
	if (!wallet || !accountId || !supportedNetwork) return

	try {
		const yearSnapshots = await sdk.perpsV3.getMarginSnapshots(accountId, {
			resolution: '1h',
			fromTimestamp: Date.now() / 1000 - PERIOD_IN_SECONDS.ONE_YEAR,
			toTimestamp: Date.now() / 1000,
		})

		const monthSnapshots = await sdk.perpsV3.getMarginSnapshots(accountId, {
			resolution: '4h',
			fromTimestamp: Date.now() / 1000 - PERIOD_IN_SECONDS.ONE_MONTH,
			toTimestamp: Date.now() / 1000,
		})

		const weekSnapshots = await sdk.perpsV3.getMarginSnapshots(accountId, {
			resolution: '1h',
			fromTimestamp: Date.now() / 1000 - PERIOD_IN_SECONDS.ONE_WEEK,
			toTimestamp: Date.now() / 1000,
		})

		return {
			snapshots: {
				[Period.ONE_YEAR]: yearSnapshots.map((s) => ({
					...s,
					margin: s.margin.toString(),
				})),
				[Period.ONE_MONTH]: monthSnapshots.map((s) => ({
					...s,
					margin: s.margin.toString(),
				})),
				[Period.ONE_WEEK]: weekSnapshots.map((s) => ({
					...s,
					margin: s.margin.toString(),
				})),
			},
			networkId,
			wallet,
		}
	} catch (err) {
		logError(err)
		throw err
	}
})

export const fetchDashboardChartDataV3 = (): AppThunk => (dispatch) => {
	dispatch(fetchMarginSnapshotsV3())
	dispatch(fetchPnlSnapshotsV3())
}

export const createConditionalOrder = createAsyncThunk<
	void,
	{
		marketId: string
		sizeDelta: Wei
		isReduceOnly: boolean
		targetPrice: Wei
		type: SmartMarginOrderType
	},
	ThunkConfig
>(
	'futures/createConditionalOrder',
	async (
		{ marketId, sizeDelta, isReduceOnly, targetPrice },
		{ getState, dispatch, extra: { sdk } }
	) => {
		const supportedNetwork = selectProviderSupportsCrossMargin(getState())
		const { network: networkId, accountId } = selectAccountContext(getState())
		const markets = selectV3Markets(getState())
		const prices = selectMarkPricesV3(getState())
		const preview = selectCrossMarginTradePreview(getState())

		if (!supportedNetwork) throw new Error('Unsupported network')
		if (!accountId) return

		const market = markets.find((m) => m.marketId === marketId)
		if (!market) throw new Error('No market found')
		const price = prices[market.marketKey]
		if (!price) throw new Error('Missing price data for market ' + market.marketKey)
		if (!preview) throw new Error('Missing trade preview')

		try {
			const orderCondition = orderConditionFromTargetPrice(targetPrice, price)
			await sdk.perpsV3.createConditionalOrders([
				{
					marketId,
					accountId,
					networkId,
					settlementStrategyId: market.settlementStrategies[0].strategyId,
					sizeDelta: sizeDelta.toBigInt(),
					acceptablePrice: preview.desiredFillPrice.toBigInt(),
					isReduceOnly: isReduceOnly,
					maxExecutorFee: DEFAULT_MAX_EXECUTOR_FEE_USD.toBigInt(),
					rawConditions: {
						[orderCondition]: targetPrice.toBigInt(),
					},
				},
			])
			dispatch(fetchConditionalOrders())
		} catch (err) {
			logError(err)
			notifyError('Failed to create order', err)
			throw err
		}
	}
)

export const cancelV3ConditionalOrders = createAsyncThunk<void, number[], ThunkConfig>(
	'futures/cancelV3ConditionalOrders',
	async (orderIds, { dispatch, getState, extra: { sdk } }) => {
		try {
			const supportedNetwork = selectProviderSupportsCrossMargin(getState())

			if (!supportedNetwork) throw new Error('Unsupported network')

			await sdk.perpsV3.cancelConditionalOrders(orderIds)
			dispatch(fetchConditionalOrders())
		} catch (err) {
			logError(err)
			notifyError('Failed to cancel order', err)
		}
	}
)

export const clearCrossMarginTradeInputs = createAsyncThunk<void, void, ThunkConfig>(
	'futures/clearCrossMarginTradeInputs',
	async (_, { dispatch }) => {
		dispatch(setTradeInputs({ nativeSize: '', susdSize: '' }))
		dispatch(setCrossMarginTradePreview({ preview: null, type: 'trade' }))
		dispatch(setEditPositionInputs({ nativeSizeDelta: '', marginDelta: '' }))
		dispatch(setLeverageInput(''))
	}
)

export const editCrossMarginTradeSize =
	(size: string, currencyType: 'usd' | 'native'): AppThunk =>
	(dispatch, getState) => {
		const indexPrice = selectMarketIndexPrice(getState())
		const tradeSide = selectLeverageSide(getState())
		const marketInfo = selectV3SelectedMarketInfo(getState())
		const tradeableMargin = selectCrossMarginTradeableMargin(getState())
		const orderType = selectTradeOrderType(getState())
		const marketPrice = selectV3SkewAdjustedPrice(getState())
		const orderPrice = selectTradePanelOrderPrice(getState())

		if (!marketInfo) throw new Error('No market selected')

		if (size === '' || indexPrice.eq(0)) {
			dispatch(setTradeInputs({ nativeSize: '', susdSize: '' }))
			dispatch(setCrossMarginTradePreview({ preview: null, type: 'trade' }))
			dispatch(setLeverageInput(''))
			return
		}

		const nativeSize =
			currencyType === 'native' ? size : String(floorNumber(wei(size).div(indexPrice), 4))
		const usdSize = currencyType === 'native' ? String(floorNumber(indexPrice.mul(size), 4)) : size
		const leverage = tradeableMargin?.gt(0) ? wei(usdSize).div(tradeableMargin.abs()) : '0'
		const sizeDeltaWei =
			tradeSide === PositionSide.LONG ? wei(nativeSize || 0) : wei(nativeSize || 0).neg()

		dispatch(
			setTradeInputs({
				susdSize: usdSize,
				nativeSize: nativeSize,
			})
		)
		dispatch(setLeverageInput(leverage.toString(2)))
		dispatch(
			stageCrossMarginTradePreview({
				market: marketInfo,
				orderPrice: wei(orderPrice || marketPrice),
				sizeDelta: sizeDeltaWei,
				action: 'trade',
				marketPrice: marketPrice,
				indexPrice: indexPrice,
				isConditional: orderType !== 'market',
			})
		)
	}

const stageCrossMarginTradePreview = createAsyncThunk<
	void,
	SnxPerpsV3TradePreviewParams,
	ThunkConfig
>('futures/stageCrossMarginTradePreview', async (inputs, { dispatch, getState }) => {
	dispatch(incrementPreviewCrossMarginCount())
	const debounceCount = getState().snxPerpsV3.previewDebounceCount
	debouncedPrepareCrossMarginTradePreview(dispatch, { ...inputs, debounceCount })
})

export const changeCrossMarginLeverageSide =
	(side: PositionSide): AppThunk =>
	(dispatch, getState) => {
		const { nativeSizeString } = selectTradePanelInputs(getState())
		dispatch(setLeverageSide(side))
		dispatch(editCrossMarginTradeSize(nativeSizeString.toString(), 'native'))
	}

export const editCrossMarginCloseAmount =
	(nativeSizeDelta: string): AppThunk =>
	(dispatch, getState) => {
		dispatch(setClosePositionSizeDelta(nativeSizeDelta))

		if (nativeSizeDelta === '' || !nativeSizeDelta) {
			dispatch(setCrossMarginTradePreview({ preview: null, type: 'close' }))
			return
		}
		const { market, marketPrice, indexPrice } = selectV3EditPositionModalInfo(getState())

		if (!market || !indexPrice) return

		try {
			const previewParams: SnxPerpsV3TradePreviewParams = {
				market: market,
				orderPrice: marketPrice,
				sizeDelta: wei(nativeSizeDelta),
				action: 'close',
				indexPrice: indexPrice,
				marketPrice: marketPrice,
			}
			dispatch(stageCrossMarginTradePreview(previewParams))
		} catch (err) {
			dispatch(handlePreviewError({ error: err.message, previewType: 'close' }))
		}
	}

// Contract Mutations

export const createPerpsV3Account = createAsyncThunk<
	{ account: string; wallet: string; network: NetworkId } | undefined,
	void,
	ThunkConfig
>(
	'futures/createPerpsV3Account',
	async (_, { getState, dispatch, extra: { sdk }, rejectWithValue }) => {
		const wallet = selectWallet(getState())
		const supportedNetwork = selectSignerSupportsCrossMargin(getState())
		const network = selectProviderNetwork(getState())
		const marginEngineEnabled = selectMargineEngineEnabled(getState())

		if (!wallet || !supportedNetwork) return undefined
		const accounts = getState().snxPerpsV3.accounts

		// Already have an account fetched and persisted for this address
		if (accounts[network]?.[wallet]?.account) {
			notifyError('There is already an account associated with this wallet')
			rejectWithValue('Account already created')
		}

		try {
			const accountIds = await sdk.perpsV3.getAccounts(wallet, marginEngineEnabled)

			if (accountIds.length > 0) {
				// Already have an account, no need to create one
				const { accountId, marginEnginePermitted } = accountIds[0]
				dispatch(
					setPerpsV3Account({
						accountId: accountId.toString(),
						marginEnginePermitted,
						wallet: wallet,
						network,
					})
				)
				return
			}

			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'create_cross_margin_account',
					hash: null,
				})
			)
			const tx = await sdk.perpsV3.createAccount()
			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(fetchPerpsV3Account())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const grantMarginEnginePermission = createAsyncThunk<void, void, ThunkConfig>(
	'futures/grantMarginEnginePermission',
	async (_, { getState, dispatch, extra: { sdk } }) => {
		const accountId = selectCrossMarginAccount(getState())

		if (!accountId) throw new Error('No account id found')

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'permit_margin_engine',
					hash: null,
				})
			)
			const tx = await sdk.perpsV3.grantMarginEnginePermission(accountId)
			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(fetchPerpsV3Account(true))
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const approveCrossMarginDeposit = createAsyncThunk<
	void,
	{ usingOneClick?: boolean },
	ThunkConfig
>(
	'futures/approveCrossMarginDeposit',
	async ({ usingOneClick = false }, { getState, dispatch, extra: { sdk, accountAbstraction } }) => {
		const marketInfo = selectV3SelectedMarketInfo(getState())
		const network = selectSignerNetwork(getState())

		if (!network) throw new Error('Unsupported network')

		const marginEngineAddress = ADDRESSES.MarginEngine[network]

		if (!marketInfo) throw new Error('Market info not found')

		if (!accountAbstraction) {
			throw new Error('Account abstraction not available')
		}

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'approve_cross_margin',
					hash: null,
				})
			)
			const request = await sdk.tokens.approveTokenSpend(
				ADDRESSES.USDC[network],
				marginEngineAddress,
				usingOneClick ? accountAbstraction.accountAddress : undefined
			)

			if (usingOneClick) {
				const paymasterServiceData = accountAbstraction.preparePaymasterData()
				if (!paymasterServiceData) {
					throw new Error('Paymaster service data not found')
				}

				// It's first transaction, need to approve paymaster too
				paymasterServiceData.skipPatchCallData = false

				const res = await accountAbstraction.sendTransactions({
					simulateTxs: [request],
					options: {
						paymasterServiceData,
					},
				})
				await monitorAndAwaitUserOp(dispatch, res)
				dispatch(fetchUsdcAbstractionAllowanceForEngine())
			} else {
				const tx = await sdk.context.walletClient.writeContract(request)
				await monitorAndAwaitTransaction(dispatch, tx)
			}
			dispatch(fetchPerpsV3Balances())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const depositWrappedTokenCrossMargin = createAsyncThunk<
	void,
	{ amount: Wei; token: 'USDC'; closeOnComplete?: boolean },
	ThunkConfig
>(
	'futures/depositWrappedTokenCrossMargin',
	async ({ amount, token, closeOnComplete }, { getState, dispatch, extra: { sdk } }) => {
		const accountId = selectCrossMarginAccount(getState())
		const network = selectSignerNetwork(getState())
		const tokenAllowances = selectTokenAllowances(getState())
		const depositAllowances = selectDepositAllowances(getState())
		const marginEnginePermitted = selectMarginEnginePermitted(getState())
		const marginEngineAddress = network ? ADDRESSES.MarginEngine[network] : undefined

		const tokenAllowance = tokenAllowances[token]

		const spotMarketAddress = network ? ADDRESSES.SpotV3MarketProxy[network] : undefined
		const perpsMarketAddress = network ? ADDRESSES.PerpsV3MarketProxy[network] : undefined
		const usdcAllowance = depositAllowances['USDC'].marginEngine
		const usdcAllowanceEth = wei(formatUnits(usdcAllowance.toBigInt(), 6))

		if (!accountId) throw new Error('Account id not found')
		if (!tokenAllowance) throw new Error('Token allowance not found for ' + token)
		if (!spotMarketAddress || !perpsMarketAddress) throw new Error('Unsupported network')

		try {
			if (!marginEngineAddress || !network) throw new Error('Unsupported network')

			if (usdcAllowanceEth?.lt(amount)) {
				dispatch(
					setTransaction({
						status: TransactionStatus.AwaitingExecution,
						type: 'approve_cross_margin',
						hash: null,
					})
				)

				const request = await sdk.tokens.approveTokenSpend(
					ADDRESSES[token][network],
					marginEngineAddress
				)
				const tx = await sdk.context.walletClient.writeContract(request)

				await monitorAndAwaitTransaction(dispatch, tx)
			}

			if (!marginEnginePermitted) {
				await dispatch(grantMarginEnginePermission())
			}

			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'deposit_cross_margin',
					hash: null,
				})
			)

			const request = await sdk.perpsV3.depositWrappedToken(
				accountId,
				'USDC',
				amount.toString(),
				true
			)

			const tx = isSimulatedRequest(request)
				? await sdk.context.walletClient.writeContract(request)
				: await sdk.context.walletClient.sendTransaction(request)

			await monitorAndAwaitTransaction(dispatch, tx)
			if (closeOnComplete) {
				dispatch(setOpenModal(null))
			}
			dispatch(fetchCrossMarginAccountData())
			dispatch(fetchMarginTransfersV3())
		} catch (err) {
			if (!isUserDeniedError(err.message)) {
				// TODO: Temporary until we have a functio to check wrapper caps
				dispatch(
					handleTransactionError({
						title: 'Global deposit limit reached',
						message:
							'Follow Kwenta updates for information on when global deposit limits will be increased.',
					})
				)
			} else {
				dispatch(handleTransactionError({ message: err.message }))
			}

			throw err
		}
	}
)

export const depositCrossMarginMargin = createAsyncThunk<
	void,
	{ amount: Wei; synthKey: V3SynthMarketKey; closeOnComplete?: boolean },
	ThunkConfig
>(
	'futures/depositCrossMarginMargin',
	async ({ amount, synthKey, closeOnComplete }, { getState, dispatch, extra: { sdk } }) => {
		const accountId = selectCrossMarginAccount(getState())
		const marginEnginePermitted = selectMarginEnginePermitted(getState())

		if (!accountId) throw new Error('Account id not found')
		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'deposit_cross_margin',
					hash: null,
				})
			)
			const request = await sdk.perpsV3.depositCollateral(
				accountId,
				V3_SYNTH_MARKET_IDS[synthKey],
				amount,
				marginEnginePermitted
			)
			const tx = await sdk.transactions.sendTransaction(request)

			await monitorAndAwaitTransaction(dispatch, tx)
			if (closeOnComplete) {
				dispatch(setOpenModal(null))
			}
			dispatch(fetchCrossMarginAccountData())
			dispatch(fetchMarginTransfersV3())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const withdrawCrossMargin = createAsyncThunk<
	void,
	{ amount: Wei; asset: V3SynthMarketKey; unwrapToToken?: 'USDC' },
	ThunkConfig
>(
	'futures/withdrawCrossMargin',
	async ({ amount, asset, unwrapToToken }, { getState, dispatch, extra: { sdk } }) => {
		const accountId = selectCrossMarginAccount(getState())
		const marginEnginePermitted = selectMarginEnginePermitted(getState())
		const marginEngineEnabled = selectMargineEngineEnabled(getState())

		if (!accountId) throw new Error('Account id not found')
		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'withdraw_cross_margin',
					hash: null,
				})
			)

			if (unwrapToToken && !marginEngineEnabled) {
				const network = selectSignerNetwork(getState())
				const depositAllowances = selectDepositAllowances(getState())

				const spotMarketAddress = network ? ADDRESSES.SpotV3MarketProxy[network] : undefined

				if (!spotMarketAddress || !network) throw new Error('Unsupported network')

				const synthSymbol = wrappedTokenToSynth(unwrapToToken)
				const synthTokenAllowance = depositAllowances[synthSymbol]

				if (depositAllowances['SUSD_V3']?.spotProxy.lt(amount)) {
					dispatch(
						setTransaction({
							status: TransactionStatus.AwaitingExecution,
							type: 'approve_cross_margin',
							hash: null,
						})
					)
					const request = await sdk.tokens.approveTokenSpend(
						ADDRESSES.SUSD_V3[network],
						spotMarketAddress
					)
					const tx = await sdk.context.walletClient.writeContract(request)

					await monitorAndAwaitTransaction(dispatch, tx)
					dispatch(fetchPerpsV3Balances())
				}
				if (synthTokenAllowance.spotProxy?.lt(amount)) {
					dispatch(
						setTransaction({
							status: TransactionStatus.AwaitingExecution,
							type: 'approve_cross_margin',
							hash: null,
						})
					)
					const request = await sdk.tokens.approveTokenSpend(
						ADDRESSES[synthSymbol][network],
						spotMarketAddress
					)
					const tx = await sdk.context.walletClient.writeContract(request)
					await monitorAndAwaitTransaction(dispatch, tx)
				}
			}

			const request = unwrapToToken
				? await sdk.perpsV3.withdrawUnwrapped(
						accountId,
						unwrapToToken,
						amount.toBigInt(),
						marginEnginePermitted
				  )
				: await sdk.perpsV3.withdrawCollateral(accountId, V3_SYNTH_MARKET_IDS[asset], amount, false)
			const tx = isSimulatedRequest(request)
				? await sdk.context.walletClient.writeContract(request)
				: await sdk.context.walletClient.sendTransaction(request)

			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(fetchCrossMarginAccountData())
			dispatch(fetchMarginTransfersV3())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const submitCrossMarginTradePanelOrder = createAsyncThunk<void, void, ThunkConfig>(
	'futures/submitCrossMarginTradePanelOrder',
	async (_, { dispatch, getState, extra: { sdk, accountAbstraction } }) => {
		const state = getState()
		const { nativeSizeDelta, stopLossPrice, takeProfitPrice, orderPrice } =
			selectTradePanelInputs(state)
		const market = selectV3SelectedMarketInfo(state)
		const accountId = selectCrossMarginAccount(state)
		const preview = selectCrossMarginTradePreview(state)
		const networkId = selectSignerNetwork(state)
		const marginEnginePermitted = selectMarginEnginePermitted(state)
		const orderType = selectTradeOrderType(state)
		const currentPrice = selectMarketIndexPrice(state)
		const requiredUsdDeposit = selectTradePanelKeeperDepositV3(state)
		const depositAllowances = selectDepositAllowances(state)
		const orders = selectCrossMarginConditionalOrders(state)
		const isOneClickTradingAvailable = selectOneClickReady(state)

		const isOneClickTradingEnabled = accountAbstraction && isOneClickTradingAvailable

		const side = selectLeverageSide(state)
		const marginEngineAddress = networkId ? ADDRESSES.MarginEngine[networkId] : undefined
		const usdAllowance = depositAllowances['USDC']?.marginEngine
		const abstractionUsdcAllowance = selectAbstractionUsdcEngineAllowance(state)

		if (!networkId) throw new Error('No signer connected')
		if (!market || !accountId || !preview) throw new Error('Invalid order submission')
		if (!marginEngineAddress) throw new Error('Unsupported network')

		const order = serializeTransactionOrder({
			marketAsset: market.asset,
			newSize: preview.newSize.abs(),
			sizeDelta: preview.sizeDelta.abs(),
			type: orderType,
			side: side,
			price: preview.fillPrice,
		})

		try {
			// Remove stale orders
			await dispatch(removeStaleOrders())

			if (isOneClickTradingEnabled) {
				if (wei(abstractionUsdcAllowance)?.lt(requiredUsdDeposit)) {
					dispatch(
						setTransaction({
							status: TransactionStatus.AwaitingExecution,
							type: 'approve_cross_margin',
							hash: null,
						})
					)
					const request = await sdk.tokens.approveTokenSpend(
						ADDRESSES.USDC[networkId],
						marginEngineAddress,
						accountAbstraction.accountAddress
					)
					const res = await accountAbstraction.sendTransactions({ simulateTxs: [request] })
					await monitorAndAwaitUserOp(dispatch, res)
				}
			} else {
				if (usdAllowance?.lt(requiredUsdDeposit)) {
					dispatch(
						setTransaction({
							status: TransactionStatus.AwaitingExecution,
							type: 'submit_cross_order',
							hash: null,
						})
					)
					const request = await sdk.tokens.approveTokenSpend(
						ADDRESSES.USDC[networkId],
						marginEngineAddress
					)
					const tx = await sdk.context.walletClient.writeContract(request)

					await monitorAndAwaitTransaction(dispatch, tx)
				}
			}

			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'submit_cross_order',
					hash: null,
					step: 'submit-trade',
					order,
				})
			)
			if (orderType === 'market') {
				const request = await sdk.perpsV3.submitOrder(
					{
						marketId: BigInt(market.marketId),
						accountId,
						sizeDelta: wei(nativeSizeDelta || 0),
						acceptablePrice: preview.desiredFillPrice,
						settlementStrategyId: market.settlementStrategies[0].strategyId,
					},
					{
						useEngine: marginEnginePermitted,
						ordersGasDeposit: requiredUsdDeposit,
					}
				)

				if (isOneClickTradingEnabled) {
					const res = await accountAbstraction.sendTransactions({ readyTxs: [request] })

					await monitorAndAwaitUserOp(dispatch, res)
				} else {
					const tx = await sdk.transactions.sendTransaction(request)

					await monitorAndAwaitTransaction(dispatch, tx)
				}
				dispatch(fetchCrossMarginAccountData())
			} else {
				if (requiredUsdDeposit.gt(0)) {
					dispatch(
						setTransaction({
							status: TransactionStatus.AwaitingExecution,
							type: 'submit_cross_order',
							hash: null,
						})
					)

					const request = await sdk.perpsV3.depositCreditForOrders(accountId, requiredUsdDeposit)

					if (isOneClickTradingEnabled) {
						const res = await accountAbstraction.sendTransactions({ readyTxs: [request] })
						await monitorAndAwaitUserOp(dispatch, res)
					} else {
						const tx = await sdk.transactions.sendTransaction(request)
						await monitorAndAwaitTransaction(dispatch, tx)
					}
				}

				if (!orderPrice?.price) throw new Error('No price set for limit / stop order')

				const orderCondition = orderConditionFromTargetPrice(orderPrice.price, currentPrice)

				const existingOrder = orders.find(
					(order) =>
						order.decodedConditions[orderCondition] &&
						wei(order.decodedConditions[orderCondition]).eq(orderPrice.price) &&
						order.orderDetails.marketId === market.marketId &&
						order.status === ConditionalOrderStatusV3.Pending &&
						!wei(order.orderDetails.sizeDelta).abs().eq(SL_TP_MAX_SIZE_CROSS_MARGIN)
				)

				const params = {
					marketId: market.marketId,
					networkId: networkId,
					accountId,
					isReduceOnly: false,
					sizeDelta: wei(nativeSizeDelta || 0).toBigInt(),
					acceptablePrice: preview.desiredFillPrice.toBigInt(),
					settlementStrategyId: market.settlementStrategies[0].strategyId,
					maxExecutorFee: DEFAULT_MAX_EXECUTOR_FEE_USD.toBigInt(),
					rawConditions: {
						[orderCondition]: orderPrice.price.toBigInt(),
					},
				}

				if (existingOrder) {
					// updateConditionalOrder with new sizeDelta (sum current into existing)
					await sdk.perpsV3.updateConditionalOrders([
						{
							orderId: existingOrder.id,
							nonce: existingOrder.nonce,
							inputs: {
								...params,
								sizeDelta: wei(nativeSizeDelta || 0)
									.add(existingOrder.orderDetails.sizeDelta)
									.toBigInt(),
							},
						},
					])
				} else {
					await sdk.perpsV3.createConditionalOrders([params])
				}

				await dispatch(fetchConditionalOrders())
			}

			const maxSizeDelta = nativeSizeDelta.gt(0)
				? SL_TP_MAX_SIZE_CROSS_MARGIN.neg()
				: SL_TP_MAX_SIZE_CROSS_MARGIN

			const sltpOrders: {
				type: RawCondition
				price: Wei
				acceptablePrice: Wei
				sizeDelta: Wei
				step: string
			}[] = []
			if (Number(stopLossPrice) > 0) {
				const desiredSLFillPrice = calculateDesiredFillPrice(
					maxSizeDelta,
					wei(stopLossPrice || 0),
					wei(DEFAULT_PRICE_IMPACT_DELTA_PERCENT.STOP_LOSS)
				)
				sltpOrders.push({
					type: nativeSizeDelta.gt(0) ? RawCondition.IsPriceBelow : RawCondition.IsPriceAbove,
					price: wei(stopLossPrice),
					acceptablePrice: desiredSLFillPrice,
					sizeDelta: maxSizeDelta,
					step: 'sign-sl',
				})
			}

			if (Number(takeProfitPrice) > 0) {
				const desiredTPFillPrice = calculateDesiredFillPrice(
					maxSizeDelta,
					wei(takeProfitPrice || 0),
					wei(DEFAULT_PRICE_IMPACT_DELTA_PERCENT.TAKE_PROFIT)
				)
				sltpOrders.push({
					type: nativeSizeDelta.gt(0) ? RawCondition.IsPriceAbove : RawCondition.IsPriceBelow,
					price: wei(takeProfitPrice),
					acceptablePrice: desiredTPFillPrice,
					sizeDelta: maxSizeDelta,
					step: 'sign-tp',
				})
			}
			if (sltpOrders.length > 0) {
				for (let i = 0; i < sltpOrders.length; i++) {
					const o = sltpOrders[i]
					dispatch(updateTransactionStep(o.step))
					await sdk.perpsV3.createConditionalOrders([
						{
							marketId: market.marketId,
							networkId: networkId,
							accountId,
							isReduceOnly: true,
							sizeDelta: o.sizeDelta.toBigInt(),
							acceptablePrice: o.acceptablePrice.toBigInt(),
							settlementStrategyId: market.settlementStrategies[0].strategyId,
							maxExecutorFee: DEFAULT_MAX_EXECUTOR_FEE_USD.toBigInt(),
							rawConditions: {
								[o.type]: o.price.toBigInt(),
							},
						},
					])
				}
			}
			await dispatch(fetchConditionalOrders())
			dispatch(fetchCrossMarginAccountData())
			dispatch(updateTransactionStep(undefined))
			dispatch(setOpenModal(null))
			dispatch(clearCrossMarginTradeInputs())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			dispatch(updateTransactionStep(undefined))
			dispatch(setOpenModal(null))
			throw err
		}
	}
)

export const submitCrossMarginSLTPUpdates = createAsyncThunk<void, void, ThunkConfig>(
	'futures/submitCrossMarginSLTPUpdates',
	async (_, { dispatch, getState, extra: { sdk, accountAbstraction } }) => {
		const { market, position } = selectV3EditPositionModalInfo(getState())
		const accountId = selectCrossMarginAccount(getState())
		const networkId = selectSignerNetwork(getState())
		const marginEnginePermitted = selectMarginEnginePermitted(getState())
		const requiredUsdDeposit = selectSLTPModalUsdDepositV3(getState())
		const existingOrders = selectAllCrossMarginSLTPOrders(getState())
		const depositAllowances = selectDepositAllowances(getState())
		const { stopLossPrice, takeProfitPrice } = selectSlTpModalInputs(getState())
		const isOneClickTradingReady = selectOneClickReady(getState())

		const isOneClickTradingEnabled = accountAbstraction && isOneClickTradingReady

		const marginEngineAddress = networkId ? ADDRESSES.MarginEngine[networkId] : undefined
		const usdAllowance = depositAllowances['USDC']?.marginEngine

		try {
			if (!marginEngineAddress) throw new Error('Unsupported network')
			if (!networkId) throw new Error('No signer connected')
			if (!market || !accountId) throw new Error('Invalid order submission')
			if (!marginEnginePermitted) throw new Error('Margin engine not permitted')
			if (!position) throw new Error('No position for orders')
			if (requiredUsdDeposit.gt(0)) {
				if (usdAllowance?.lt(requiredUsdDeposit) && !isOneClickTradingEnabled) {
					dispatch(
						setTransaction({
							status: TransactionStatus.AwaitingExecution,
							type: 'submit_cross_order',
							hash: null,
						})
					)
					const request = await sdk.tokens.approveTokenSpend(
						ADDRESSES.USDC[networkId],
						marginEngineAddress
					)
					const tx = await sdk.context.walletClient.writeContract(request)
					await monitorAndAwaitTransaction(dispatch, tx)
				}
				dispatch(
					setTransaction({
						status: TransactionStatus.AwaitingExecution,
						type: 'submit_cross_order',
						hash: null,
					})
				)

				const request = await sdk.perpsV3.depositCreditForOrders(accountId, requiredUsdDeposit)
				if (isOneClickTradingEnabled) {
					const res = await accountAbstraction.sendTransactions({ readyTxs: [request] })
					await monitorAndAwaitUserOp(dispatch, res)
				} else {
					const tx = await sdk.transactions.sendTransaction(request)
					await monitorAndAwaitTransaction(dispatch, tx)
				}
			}

			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'submit_cross_order',
					hash: null,
				})
			)

			const stopLoss = existingOrders.find(
				(o) => o.marketKey === market.marketKey && o.orderType === ConditionalOrderTypeEnum.STOP
			)
			const takeProfit = existingOrders.find(
				(o) => o.marketKey === market.marketKey && o.orderType === ConditionalOrderTypeEnum.LIMIT
			)

			const stopLossExists = !!stopLoss
			const takeProfitExists = !!takeProfit

			const stopLossPriceNotEmpty = stopLossPrice !== ''
			const takeProfitPriceNotEmpty = takeProfitPrice !== ''

			const stopLossChanged =
				(!stopLossExists && stopLossPriceNotEmpty) ||
				(stopLossExists && (stopLossPrice === '' || !stopLoss.targetPrice.eq(wei(stopLossPrice))))

			const takeProfitChanged =
				(!takeProfitExists && takeProfitPriceNotEmpty) ||
				(takeProfitExists &&
					(takeProfitPrice === '' || !takeProfit.targetPrice.eq(wei(takeProfitPrice))))

			const isLong = position?.details.side === PositionSide.LONG

			const maxSizeDelta = isLong ? SL_TP_MAX_SIZE_CROSS_MARGIN.neg() : SL_TP_MAX_SIZE_CROSS_MARGIN

			const sltpOrders: {
				updateOrder?: {
					orderId: number
					nonce: number
					status?: ConditionalOrderStatusV3
					inputs: ConditionalOrderInput
				}
				cancelOrder?: {
					orderId: number
					nonce: number
				}
				createOrder?: ConditionalOrderInput
			}[] = []

			const sharedParams = {
				marketId: market.marketId,
				networkId: networkId,
				accountId,
				isReduceOnly: true,
				sizeDelta: maxSizeDelta.toBigInt(),
				settlementStrategyId: market.settlementStrategies[0].strategyId,
				maxExecutorFee: DEFAULT_MAX_EXECUTOR_FEE_USD.toBigInt(),
			}

			if (stopLossChanged) {
				if (stopLossPriceNotEmpty) {
					const desiredSLFillPrice = calculateDesiredFillPrice(
						maxSizeDelta,
						wei(stopLossPrice || 0),
						wei(DEFAULT_PRICE_IMPACT_DELTA_PERCENT.STOP_LOSS)
					)
					const conditionType = isLong ? RawCondition.IsPriceBelow : RawCondition.IsPriceAbove
					sltpOrders.push(
						stopLoss
							? {
									updateOrder: {
										orderId: stopLoss.id,
										nonce: stopLoss.nonce!,
										inputs: {
											...sharedParams,
											acceptablePrice: desiredSLFillPrice.toBigInt(),
											rawConditions: {
												[conditionType]: wei(stopLossPrice).toBigInt(),
											},
										},
									},
							  }
							: {
									createOrder: {
										...sharedParams,
										acceptablePrice: desiredSLFillPrice.toBigInt(),
										rawConditions: {
											[conditionType]: wei(stopLossPrice).toBigInt(),
										},
									},
							  }
					)
				} else {
					sltpOrders.push({
						cancelOrder: {
							orderId: stopLoss!.id,
							nonce: stopLoss!.nonce!,
						},
					})
				}
			}

			if (takeProfitChanged) {
				if (takeProfitPriceNotEmpty) {
					const desiredTPFillPrice = calculateDesiredFillPrice(
						maxSizeDelta,
						wei(takeProfitPrice || 0),
						wei(DEFAULT_PRICE_IMPACT_DELTA_PERCENT.TAKE_PROFIT)
					)
					const conditionType = isLong ? RawCondition.IsPriceAbove : RawCondition.IsPriceBelow
					sltpOrders.push(
						takeProfit
							? {
									updateOrder: {
										orderId: takeProfit.id,
										nonce: takeProfit.nonce!,
										inputs: {
											...sharedParams,
											acceptablePrice: desiredTPFillPrice.toBigInt(),
											rawConditions: {
												[conditionType]: wei(takeProfitPrice).toBigInt(),
											},
										},
									},
							  }
							: {
									createOrder: {
										...sharedParams,
										acceptablePrice: desiredTPFillPrice.toBigInt(),
										rawConditions: {
											[conditionType]: wei(takeProfitPrice).toBigInt(),
										},
									},
							  }
					)
				} else {
					sltpOrders.push({
						cancelOrder: takeProfit?.nonce
							? {
									orderId: takeProfit?.id,
									nonce: takeProfit?.nonce,
							  }
							: undefined,
					})
				}
			}

			const updates = sltpOrders.filter((o) => !!o.updateOrder)
			const creates = sltpOrders.filter((o) => !!o.createOrder)
			const cancels = sltpOrders.filter((o) => !!o.cancelOrder)

			if (sltpOrders.length > 0) {
				dispatch(
					setTransaction({
						status: TransactionStatus.AwaitingExecution,
						type: 'submit_cross_order',
						hash: null,
					})
				)

				if (updates.length > 0) {
					await sdk.perpsV3.updateConditionalOrders(
						updates.map((o) => ({
							orderId: o.updateOrder!.orderId,
							nonce: o.updateOrder!.nonce,
							inputs: o.updateOrder!.inputs,
							status: o.updateOrder?.status,
						}))
					)
				}
				if (cancels.length > 0) {
					await sdk.perpsV3.cancelConditionalOrders(cancels.map((o) => o.cancelOrder!.orderId))
				}
				if (creates.length > 0) {
					await sdk.perpsV3.createConditionalOrders(creates.map((o) => o.createOrder!))
				}
			}

			dispatch(updateTransactionStatus(TransactionStatus.Confirmed))
			dispatch(fetchConditionalOrders())
			dispatch(fetchCrossMarginAccountData())
			dispatch(setShowPositionModal(null))
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const withdrawCMAccountKeeperBalance = createAsyncThunk<void, Wei, ThunkConfig>(
	'futures/withdrawAccountKeeperBalance',
	async (amount, { getState, dispatch, extra: { sdk } }) => {
		const address = selectCrossMarginAccount(getState())
		try {
			if (!address) throw new Error('No cross margin account')
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'withdraw_keeper_balance',
					hash: null,
				})
			)

			const tx = await sdk.perpsV3.withdrawCreditForOrders(address, amount)
			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(fetchPerpsV3Balances())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const depositCMAccountKeeperBalance = createAsyncThunk<void, Wei, ThunkConfig>(
	'futures/depositAccountKeeperBalance',
	async (amount, { getState, dispatch, extra: { sdk, accountAbstraction } }) => {
		const address = selectCrossMarginAccount(getState())
		const isOneClickTradingEnabled = selectOneClickReady(getState())

		try {
			if (!address) throw new Error('No cross margin account')

			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'deposit_keeper_balance',
					hash: null,
				})
			)

			const request = await sdk.perpsV3.depositCreditForOrders(address, amount)

			if (accountAbstraction && isOneClickTradingEnabled) {
				const res = await accountAbstraction.sendTransactions({ readyTxs: [request] })

				await monitorAndAwaitUserOp(dispatch, res)
				dispatch(fetchAbstractionUsdcBalance())
			} else {
				const tx = await sdk.transactions.sendTransaction(request)

				await monitorAndAwaitTransaction(dispatch, tx)
				dispatch(fetchPerpsV3Balances())
			}
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const submitCrossMarginReducePositionOrder = createAsyncThunk<void, void, ThunkConfig>(
	'futures/submitCrossMarginReducePositionOrder',
	async (_, { getState, dispatch, extra: { sdk, accountAbstraction } }) => {
		const networkId = selectSignerNetwork(getState())
		const { market, position, marketPrice } = selectV3EditPositionModalInfo(getState())
		const accountId = selectCrossMarginAccount(getState())
		const { nativeSizeDelta, orderType, price } = selectClosePositionOrderInputs(getState())
		const preview = selectCloseCMPositionPreview(getState())
		const marginEnginePermitted = selectMarginEnginePermitted(getState())
		const requiredUsdDeposit = selectCloseModalKeeperDepositV3(getState())
		const depositAllowances = selectDepositAllowances(getState())
		const isOneClickTradingReady = selectOneClickReady(getState())

		const isOneClickTradingEnabled = accountAbstraction && isOneClickTradingReady

		const usdAllowance = depositAllowances['USDC']?.marginEngine

		try {
			if (!position) throw new Error('Missing position data')
			if (!accountId) throw new Error('Account not found')
			if (!preview) throw new Error('Missing trade preview')
			if (!networkId) throw new Error('Missing network id')
			if (!market) throw new Error('Missing market')

			const order = serializeTransactionOrder({
				marketAsset: market.asset,
				newSize: preview.newSize.abs(),
				sizeDelta: preview.sizeDelta.abs(),
				type: orderType,
				side: preview.side,
				price: preview.fillPrice,
			})

			if (orderType !== 'market') {
				if (!price) throw new Error('Missing price')
				const orderCondition = orderConditionFromTargetPrice(wei(price.value), marketPrice)

				if (requiredUsdDeposit.gt(0)) {
					if (usdAllowance?.lt(requiredUsdDeposit) && !isOneClickTradingEnabled) {
						const marginEngineAddress = networkId ? ADDRESSES.MarginEngine[networkId] : undefined
						if (!marginEngineAddress) throw new Error('Unsupported network')

						dispatch(
							setTransaction({
								status: TransactionStatus.AwaitingExecution,
								type: 'submit_cross_order',
								hash: null,
								order,
							})
						)
						const request = await sdk.tokens.approveTokenSpend(
							ADDRESSES.USDC[networkId],
							marginEngineAddress
						)
						const tx = await sdk.context.walletClient.writeContract(request)
						await monitorAndAwaitTransaction(dispatch, tx)
					}

					dispatch(
						setTransaction({
							status: TransactionStatus.AwaitingExecution,
							type: 'submit_cross_order',
							hash: null,
							order,
						})
					)
					const request = await sdk.perpsV3.depositCreditForOrders(accountId, requiredUsdDeposit)
					if (isOneClickTradingEnabled) {
						const res = await accountAbstraction.sendTransactions({ readyTxs: [request] })
						await monitorAndAwaitUserOp(dispatch, res)
					} else {
						const tx = await sdk.transactions.sendTransaction(request)
						await monitorAndAwaitTransaction(dispatch, tx)
					}
				}

				await sdk.perpsV3.createConditionalOrders([
					{
						marketId: market.marketId,
						accountId,
						networkId,
						settlementStrategyId: market.settlementStrategies[0].strategyId,
						sizeDelta: wei(nativeSizeDelta).toBigInt(),
						acceptablePrice: preview.desiredFillPrice.toBigInt(),
						isReduceOnly: true,
						maxExecutorFee: DEFAULT_MAX_EXECUTOR_FEE_USD.toBigInt(),
						rawConditions: {
							[orderCondition]: wei(price.value).toBigInt(),
						},
					},
				])
			} else {
				dispatch(
					setTransaction({
						status: TransactionStatus.AwaitingExecution,
						type: 'close_cross_margin',
						hash: null,
						order,
					})
				)
				const request = await sdk.perpsV3.submitOrder(
					{
						marketId: BigInt(market.marketId),
						accountId,
						sizeDelta: wei(nativeSizeDelta || 0),
						acceptablePrice: preview.desiredFillPrice,
						settlementStrategyId: market.settlementStrategies[0].strategyId,
					},
					{
						useEngine: marginEnginePermitted,
					}
				)

				if (accountAbstraction && isOneClickTradingReady) {
					const res = await accountAbstraction.sendTransactions({ readyTxs: [request] })
					await monitorAndAwaitUserOp(dispatch, res)
				} else {
					const tx = await sdk.transactions.sendTransaction(request)
					await monitorAndAwaitTransaction(dispatch, tx)
				}
			}

			dispatch(fetchCrossMarginPendingOrders())
			dispatch(fetchCrossMarginAccountData())
			dispatch(setShowPositionModal(null))
			dispatch(clearCrossMarginTradeInputs())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
		// TODO: hookup submit v3 order
	}
)

export const executeAsyncOrder = createAsyncThunk<void, `0x${string}`, ThunkConfig>(
	'futures/executeAsyncOrder',
	async (originTxHash, { getState, extra: { sdk } }) => {
		const account = selectCrossMarginAccount(getState())
		if (!account) throw new Error('No wallet connected')

		const request = await sdk.perpsV3.executeAsyncOrder(account)
		const txHash = await sdk.context.walletClient.sendTransaction(request)
		await monitorFollowingTransaction(originTxHash, txHash)
	}
)

export const approveSpotMarketSpender = createAsyncThunk<
	void,
	{ asset: DepositableAssetKeysV3 },
	ThunkConfig
>('futures/approveSpotMarketSpender', async ({ asset }, { getState, dispatch, extra: { sdk } }) => {
	const marketInfo = selectV3SelectedMarketInfo(getState())
	const network = selectSignerNetwork(getState())

	const spotMarketAddress = network ? ADDRESSES.SpotV3MarketProxy[network] : undefined
	if (!spotMarketAddress || !network) throw new Error('No spot market address found')
	if (!marketInfo) throw new Error('Market info not found')
	try {
		dispatch(
			setTransaction({
				status: TransactionStatus.AwaitingExecution,
				type: 'approve_cross_margin',
				hash: null,
			})
		)
		const request = await sdk.tokens.approveTokenSpend(ADDRESSES[asset][network], spotMarketAddress)
		const tx = await sdk.context.walletClient.writeContract(request)
		await monitorAndAwaitTransaction(dispatch, tx)
		dispatch(fetchPerpsV3Balances())
	} catch (err) {
		dispatch(handleTransactionError({ message: err.message }))
		throw err
	}
})

export const approvePerpsMarketSpender = createAsyncThunk<
	void,
	{ asset: 'USDC' | 'SUSD_V3' },
	ThunkConfig
>(
	'futures/approvePerpsMarketSpender',
	async ({ asset }, { getState, dispatch, extra: { sdk } }) => {
		const marketInfo = selectV3SelectedMarketInfo(getState())
		const network = selectSignerNetwork(getState())

		const perpsMarketAddress = network ? ADDRESSES.PerpsV3MarketProxy[network] : undefined
		if (!perpsMarketAddress || !network) throw new Error('No perps proxy address found')
		if (!marketInfo) throw new Error('Market info not found')
		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'approve_cross_margin',
					hash: null,
				})
			)
			const request = await sdk.tokens.approveTokenSpend(
				ADDRESSES[asset][network],
				perpsMarketAddress
			)
			const tx = await sdk.context.walletClient.writeContract(request)
			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(fetchPerpsV3Balances())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const editCrossMarginTradeOrderPrice =
	(price: string): AppThunk =>
	(dispatch, getState) => {
		const rate = selectV3SkewAdjustedPrice(getState())
		const orderType = selectTradeOrderType(getState())
		const side = selectLeverageSide(getState())
		const inputs = selectTradePanelInputs(getState())
		dispatch(setTradePanelOrderPrice(price))
		const invalidLabel = orderPriceInvalidLabel(price, side, rate, orderType)
		dispatch(setOrderPriceInvalidLabel(invalidLabel))
		if (!invalidLabel && price && inputs.susdSize) {
			// Recalc the trade
			dispatch(editCrossMarginTradeSize(inputs.susdSizeString, 'usd'))
		}
	}

export const fetchFundingRatesHistoryV3 = createAsyncThunk<
	{ marketAsset: FuturesMarketAsset; rates: any },
	{ marketAsset: FuturesMarketAsset; period: Period },
	ThunkConfig
>('futures/fetchFundingRatesHistoryV3', async ({ marketAsset, period }, { extra: { sdk } }) => {
	const rates = await sdk.perpsV3.getMarketFundingRatesHistory(
		marketAsset,
		PERIOD_IN_SECONDS[period]
	)
	return { marketAsset, rates }
})

// Delegate
export const addDelegateForCMAccount = createAsyncThunk<void, void, ThunkConfig>(
	'futures/addDelegateForCMAccount',
	async (_, { getState, dispatch, extra: { sdk } }) => {
		const state = getState()
		const account = selectCrossMarginAccount(state)
		const wallet = selectWallet(state)
		const network = selectSignerNetwork(state)
		const delegatedAddress = selectSelectedDelegationAddress(getState())
		const newNickname = selectNewNickname(getState())

		if (!account) {
			notifyError('No cross margin account')
			return
		}

		if (!wallet) {
			notifyError('No wallet connected')
			return
		}

		if (!network) {
			notifyError('No network selected')
			return
		}

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'add_delegate',
					hash: null,
				})
			)
			const tx = await sdk.perpsV3.grantDelegatePermission(account, delegatedAddress)
			const delegate = { address: delegatedAddress, nickname: newNickname }
			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(addDelegateToAddressBook({ wallet, delegate }))
			dispatch(
				addDelegateToCMDelegates({
					wallet,
					network,
					delegate: { address: delegatedAddress, nickname: newNickname },
				})
			)
			dispatch(fetchDelegatesForCMAccount())
			dispatch(fetchSubAccountsForCMAccount())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const removeDelegateForCMAccount = createAsyncThunk<void, void, ThunkConfig>(
	'futures/removeDelegateForCMAccount',
	async (_, { getState, dispatch, extra: { sdk } }) => {
		const state = getState()
		const wallet = selectWallet(state)
		const account = selectCrossMarginAccount(state)
		const network = selectSignerNetwork(state)
		const delegatedAddress = selectSelectedDelegationAddress(getState())

		if (!account) {
			notifyError('No cross margin account')
			return
		}

		if (!wallet) {
			notifyError('No wallet connected')
			return
		}

		if (!delegatedAddress) {
			notifyError('No delegate selected')
			return
		}

		if (!network) {
			notifyError('No network selected')
			return
		}

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'remove_delegate',
					hash: null,
				})
			)
			const tx = await sdk.perpsV3.revokeDelegatePermission(account, delegatedAddress)
			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(removeDelegateFromAddressBook({ wallet, delegatedAddress }))
			dispatch(removeDelegateFromCMDelegates({ wallet, network, delegatedAddress }))
			dispatch(fetchDelegatesForCMAccount())
			dispatch(fetchSubAccountsForCMAccount())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const fetchDelegatesForCMAccount = createAsyncThunk<
	| {
			delegatesWithNicknames: DelegationAccountInfo[]
			wallet: string
			network: NetworkId
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchDelegatesForCMAccount', async (_, { getState, extra: { sdk } }) => {
	const wallet = selectSignerWallet(getState())
	const network = selectProviderNetwork(getState())
	const futuresSupported = selectProviderSupportsCrossMargin(getState())
	const addressBook = selectAddressBook(getState())

	if (!futuresSupported || !wallet) return

	try {
		const delegates = await sdk.perpsV3.getDelegatesForAccount(wallet)
		const addressMap = Object.fromEntries(
			addressBook.map((d) => [getAddress(d.address), d.nickname])
		)
		const delegatesWithNicknames = delegates.map(({ delegate }) => {
			const address = getAddress(delegate)
			const nickname = addressMap[address] || 'New Delegate'
			return { address, nickname }
		}) as DelegationAccountInfo[]

		return {
			wallet,
			network,
			delegatesWithNicknames,
		}
	} catch (err) {
		notifyError('Failed to fetch delegates', err)
		throw err
	}
})

export const fetchSubAccountsForCMAccount = createAsyncThunk<
	| {
			subAccountsWithNicknames: DelegationAccountInfo[]
			wallet: string
			network: NetworkId
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchSubAccountsForCMAccount', async (_, { getState, extra: { sdk } }) => {
	const wallet = selectSignerWallet(getState())
	const network = selectProviderNetwork(getState())
	const futuresSupported = selectProviderSupportsCrossMargin(getState())
	const addressBook = selectAddressBook(getState())

	if (!futuresSupported || !wallet) return

	try {
		const subAccounts = await sdk.perpsV3.getSubAccountsForAccount(wallet)
		const addressMap = Object.fromEntries(
			addressBook.map((d) => [getAddress(d.address), d.nickname])
		)
		const subAccountsWithNicknames = subAccounts.map(({ caller }) => {
			const address = getAddress(caller)
			const nickname = addressMap[address] || 'New Sub-Account'
			return { address, nickname }
		}) as DelegationAccountInfo[]
		return {
			wallet,
			network,
			subAccountsWithNicknames,
		}
	} catch (err) {
		notifyError('Failed to fetch sub-accounts', err)
		throw err
	}
})

export const editCrossMarginPositionSize =
	(marketKey: FuturesMarketKey, nativeSizeDelta: string): AppThunk =>
	(dispatch, getState) => {
		const { marketPrice, market, indexPrice } = selectV3EditPositionModalInfo(getState())
		if (!market || !indexPrice) throw new Error('Missing market data')
		try {
			dispatch(
				stageCrossMarginTradePreview({
					market,
					orderPrice: marketPrice,
					sizeDelta: wei(nativeSizeDelta || 0),
					action: 'edit',
					indexPrice,
					marketPrice: marketPrice,
				})
			)
		} catch (err) {
			dispatch(handlePreviewError({ error: err.message, previewType: 'edit' }))
		}
	}

export const submitCrossMarginAdjustPositionSize = createAsyncThunk<void, boolean, ThunkConfig>(
	'futures/submitCrossMarginAdjustPositionSize',
	async (overridePriceProtection, { getState, dispatch, extra: { sdk, accountAbstraction } }) => {
		const state = getState()
		const { market } = selectV3EditPositionModalInfo(state)
		const account = selectCrossMarginAccount(state)
		const preview = selectEditPositionPreview(state)
		const { nativeSizeDelta } = selectEditPositionInputs(state)
		const isOneClickTradingEnabled = selectOneClickReady(state)
		const marginEnginePermitted = selectMarginEnginePermitted(state)

		try {
			if (!market) throw new Error('Market info not found')
			if (!account) throw new Error('No smart margin account found')
			if (!nativeSizeDelta || nativeSizeDelta === '') throw new Error('No margin amount set')
			if (!preview) throw new Error('Missing trade preview')
			if (!overridePriceProtection && preview.exceedsPriceProtection) {
				throw new Error('Price impact exceeds price protection')
			}

			const settlementStrategyId = market.settlementStrategies[0]?.strategyId
			if (!settlementStrategyId && settlementStrategyId !== 0)
				throw new Error('No settlement strategy found')

			const order = serializeTransactionOrder({
				marketAsset: market.asset,
				newSize: preview.newSize.abs(),
				sizeDelta: preview.sizeDelta.abs(),
				type: 'market',
				side: preview.side,
				price: preview.fillPrice,
			})

			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'submit_cross_order',
					hash: null,
					order,
				})
			)

			const request = await sdk.perpsV3.submitOrder(
				{
					accountId: account,
					marketId: BigInt(market.marketId),
					sizeDelta: wei(nativeSizeDelta),
					acceptablePrice: preview.desiredFillPrice,
					settlementStrategyId: settlementStrategyId,
				},
				{
					useEngine: marginEnginePermitted,
				}
			)

			if (accountAbstraction && isOneClickTradingEnabled) {
				const res = await accountAbstraction.sendTransactions({ readyTxs: [request] })
				await monitorAndAwaitUserOp(dispatch, res)
			} else {
				const tx = await sdk.transactions.sendTransaction(request)
				await monitorAndAwaitTransaction(dispatch, tx)
			}

			dispatch(setShowPositionModal(null))
			dispatch(clearCrossMarginTradeInputs())
			dispatch(fetchCrossMarginAccountData())
			dispatch(clearCrossMarginTradeInputs())
		} catch (err) {
			logError(err)
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

export const editCloseCMPositionPrice =
	(price: string): AppThunk =>
	(dispatch, getState) => {
		const state = getState()
		const { nativeSizeDelta, orderType } = selectClosePositionOrderInputs(state)
		const { position, marketPrice, market, indexPrice } = selectV3EditPositionModalInfo(state)

		if (!market || !indexPrice) throw new Error('No market data')

		const closeTradeSide =
			position?.details.side === PositionSide.SHORT ? PositionSide.LONG : PositionSide.SHORT
		const invalidLabel = orderPriceInvalidLabel(
			price || '0',
			closeTradeSide,
			marketPrice,
			orderType
		)

		dispatch(setClosePositionPrice({ value: price, invalidLabel }))

		try {
			dispatch(
				stageCrossMarginTradePreview({
					market: market,
					orderPrice: isNaN(Number(price)) || !price ? marketPrice : wei(price),
					sizeDelta: wei(nativeSizeDelta || 0),
					action: 'close',
					isConditional: orderType !== 'market',
					indexPrice,
					marketPrice: marketPrice,
				})
			)
		} catch (err) {
			dispatch(handlePreviewError({ error: err.message, previewType: 'close' }))
		}
	}

export const fetchCrossMarginOrderHistory = createAsyncThunk<
	| {
			orders: FuturesOrderHistory[]
			networkId: NetworkId
			wallet: string
	  }
	| undefined,
	void,
	ThunkConfig
>('futures/fetchCrossMarginOrderHistory', async (_, { getState, extra: { sdk } }) => {
	const wallet = selectWallet(getState())
	const networkId = selectProviderNetwork(getState())
	const account = selectCrossMarginAccount(getState())
	const futuresSupported = selectProviderSupportsCrossMargin(getState())
	if (!futuresSupported || !wallet || !account) return
	const orders = await sdk.perpsV3.getOrderHistory(account)
	return { orders, networkId, wallet }
})

export const fetchUsdcAbstractionAllowanceForEngine = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchUsdcAbstractionAllowanceForEngine',
	async (_, { dispatch, getState, extra: { sdk, accountAbstraction } }) => {
		const networkId = selectSignerNetwork(getState())
		const { accountAddress } = accountAbstraction || {}
		if (!networkId) throw new Error('No network id')
		if (!accountAddress) throw new Error('No account abstraction address')

		const engineAddress = ADDRESSES.MarginEngine[networkId]

		const allowance = await sdk.tokens.checkAllowance(
			ADDRESSES.USDC[networkId],
			engineAddress,
			accountAddress
		)

		dispatch(setAbstractionUsdcEngineAllowance(allowance.toString()))
	}
)
